import ContentTypeFilter from 'components/ContentTypeFilter'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'

import { mdiSortAscending, mdiSortVariant } from '@mdi/js'
import Icon from '@mdi/react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import PPSelect from 'components/PPSelect'
import styles from './FileFolderViewHeader.pcss'
import { FilesBulkActions, FilesBulkActionsProps } from './FilesBulkActions'

export interface FileFolderViewHeaderProps {
  folderName: string
  folderId: string
  activeFilter: string
  filters: string[]
  title?: React.ReactNode
  navPrefix?: string
  backUrl?: string
  actionsDisabled?: boolean
  sorting?: {
    sortBy: 'name' | 'date'
    sortDirection: 'asc' | 'desc'
    onChange: (sortBy: 'name' | 'date', sortDirection: 'asc' | 'desc') => void
  }
  bulkActions?: FilesBulkActionsProps
  staticHeader?: boolean
  hideTypeFilter?: boolean
  onSeeAll?: (folderId: string) => void
  onFilterChange: (filter: string) => void
}

export function FileFolderViewHeader(props: FileFolderViewHeaderProps) {
  const { actionsDisabled, sorting } = props
  const { sortBy, sortDirection, onChange } = sorting || {}

  const onSortByChange = (value: string) => {
    if (onChange && sortDirection) {
      onChange(value as 'name' | 'date', sortDirection)
    }
  }

  const toggleSortDirection = () => {
    if (onChange && sortBy) {
      onChange(sortBy, sortDirection === 'asc' ? 'desc' : 'asc')
    }
  }

  const toggleSortTooltip = React.useMemo(() => {
    if (sortBy === 'name') {
      return sortDirection === 'asc' ? 'Z-A' : 'A-Z'
    }
    return sortDirection === 'asc' ? 'Oldest first' : 'Newest first'
  }, [sortBy, sortDirection])

  return (
    <header className={`${styles.header} ${props.staticHeader ? styles['header-static'] : ''}`}>
      {
        props.title ? props.title : (
          <React.Fragment>
            <h2 className={`${styles.title} text-ellipsis`}>{props.folderName}</h2>
            {
              props.onSeeAll ? (
                <button
                  type="button"
                  className={`${styles.link} ${styles['link-btn']}`}
                  onClick={() => props.onSeeAll?.(props.folderId)}
                >
                  <FormattedMessage id="general.carousel.nav.see-all" />
                </button>
              ) : props.navPrefix && (
                <Link className={styles.link} to={`${props.navPrefix}/${props.folderId}`}>
                  <FormattedMessage id="general.carousel.nav.see-all" />
                </Link>
              )
            }
            {
              props.backUrl && (
                <Link className={styles.link} to={props.backUrl}>
                  <FormattedMessage id="general.carousel.nav.back" />
                </Link>
              )
            }
          </React.Fragment>
        )
      }
      {props.filters.length > 1 && !props.hideTypeFilter && (
        <ContentTypeFilter
          renderSingle
          all={props.filters.length > 1}
          options={props.filters}
          selected={props.activeFilter}
          className={styles.typeFilter}
          onFilterSelected={props.onFilterChange}
        />
      )}
      {props.sorting && (
        <div className={styles['sort-box']}>
          <PPSelect
            options={{ date: { label: 'Date added' }, name: { label: 'Name' } }}
            selectedValue={props.sorting.sortBy}
            name="Sort by"
            id="files-sort-by-select"
            raised
            icon={<Icon path={mdiSortVariant} size="20px" />}
            className={styles.select}
            disabled={actionsDisabled}
            onSelectionChange={onSortByChange}
          />
          <Tooltip title={toggleSortTooltip}>
            <IconButton
              size="small"
              className={`${styles['btn-sort-dir']} solid`}
              data-testid="sort-direction-toggle"
              disabled={actionsDisabled}
              onClick={toggleSortDirection}
            >
              <Icon
                path={mdiSortAscending}
                size="20px"
                className={`${styles['sort-icon']} ${props.sorting.sortDirection === 'asc' ? styles.flip : ''}`}
              />
            </IconButton>
          </Tooltip>
        </div>
      )}
      {props.bulkActions && (
        <FilesBulkActions 
          {...props.bulkActions}
          disabled={typeof actionsDisabled === 'boolean' ? actionsDisabled : props.bulkActions.disabled}
        />
      )}
    </header>
  )
}

export default FileFolderViewHeader
